import { type CheckboxProps, type PaginationProps } from "@mantine/core";
import { type ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { useSummaryFilters } from "src/hooks/useSummaryFilters";
import { ICol, type ICheckList, type ISection } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setCheckLists,
  setUsers,
} from "src/redux/slices/entriesGeneratorSlice";
import { setFiles } from "src/redux/slices/generatorSlice";
import {
  addToVerify,
  removeFromVerify,
  resetVerifySlice,
} from "src/redux/slices/verifySlice";
import { getYachtId, MEDLBID } from "src/utils";
import { getWidgetSections } from "src/components/pages/Home/CustomDashboard/Widget.utils";
import { useSetComments } from "../Summary.hooks";
import { buildSummaryRequestBody } from "src/components/pages/Summary/AdvancedFiltersModal/AdvancedFiltersModal";

export const useVerifyEntries = () => {
  const dispatch = useDispatch();
  const { idType, lbeidOrPartId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toVerify } = useSelector(state => state.verifySlice);
  const filters = useSummaryFilters();

  const pendingVerify = useSelector(
    state => state.pendingRequestsSlice.pendingVerify,
  );

  const isPendingVerify = useMemo(
    () =>
      !!pendingVerify.find(p => p[String(idType) as "lbeid"] === lbeidOrPartId),
    [idType, lbeidOrPartId, pendingVerify],
  );

  const mapReviewEntry = useCallback(
    (data: any) => {
      dispatch(setFiles(data.data?.attachments ?? []));
      if (data.data?.lbentry?.checklists) {
        dispatch(
          setCheckLists(
            Object.entries(data.data.lbentry.checklists).map(
              ([key, value]) => ({
                cid: key,
                ...(value as Omit<ICheckList, "cid">),
              }),
            ),
          ),
        );
      }
      const reviewDataReturn = {
        lbEntry: data.data?.lbentry,
        sections: (data.data?.lbentry?.sections as ISection[]) ?? [],
        verifiable: data.data?.verifiable,
        preview: data.data?.preview,
        lbname: data.data?.lbname,
        entryref: data.data?.enum,
        restricted: data.data?.restricted,
        authorized: data.data?.authorized,
        lbid: data.data?.lbid,
        idata: data.data?.idata,
        deleted: data.data?.deleted,
        status: data.data?.status,
        comments: data.data?.comments,
        secdata: data.data?.secdata ?? null,
        eventStatus: data.data?.eventstatus,
        entryDate:
          data.data?.entrydate ||
          data.data?.datecreated ||
          data.data?.createdDate,
        entryDateUTC: data.data?.entrydateUTC,
      };
      if (data.data?.weid) {
        return {
          ...reviewDataReturn,
          sections: getWidgetSections(data?.data?.entry?.sections),
        };
      }
      return reviewDataReturn;
    },
    [dispatch],
  );

  const mapUsers = useCallback(
    (data: any) => {
      dispatch(setUsers(data.data.review.users));
      const users = data.data.review.data.map(
        (entry: {
          meid?: string;
          partid?: string;
          lbeid: string;
          weid?: string;
        }) => ({
          id: entry.weid ?? entry.meid ?? entry.partid ?? entry.lbeid,
          idType: entry.weid
            ? "weid"
            : entry.meid
              ? "meid"
              : entry.partid
                ? "partid"
                : entry.lbeid
                  ? "lbeid"
                  : "",
        }),
      ) as Array<{ id: string; idType: string }>;
      return { users };
    },

    [dispatch],
  );

  const getRequestParams = useCallback(() => {
    const params: any = {
      yachtid: getYachtId(),
    };

    if (idType && lbeidOrPartId) {
      params[String(idType)] = lbeidOrPartId;
    }

    return params;
  }, [idType, lbeidOrPartId]);

  const { data, refetch: fetchEntry } = useGetApi({
    request: "get-review-entry",
    body: {
      params: getRequestParams(),
    },
    mapper: mapReviewEntry,
    fetchOnInit: false,
  });
  const setComments = useSetComments();

  useEffect(() => {
    if (data?.comments) {
      setComments(data.comments);
    }
  }, [data?.comments]);

  const { data: reviewData, refetch: fetchUsers } = useGetApi({
    request: "get-review",
    body: {
      ...buildSummaryRequestBody(
        {
          logbook: filters.logbook,
          status: filters.status.map(String),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          ref: filters.ref,
          hasSnapshot: filters.hasSnapshot,
          hasAttachments: filters.hasAttachments,
          hasChecklists: filters.hasChecklists,
          sortBy: filters.sort,
          direction: (filters.dir as "asc" | "desc") ?? "asc",
        },
        filters.entriesType,
      ),
    },
    mapper: mapUsers,
    fetchOnInit: false,
  });
  const fetch = useCallback(async () => {
    await Promise.all([fetchEntry(), fetchUsers()]);
  }, [fetchEntry, fetchUsers]);

  const fetchRestricted = useCallback(
    async (tid: string) => {
      await fetchEntry({ params: { tid } });
    },
    [fetchEntry],
  );

  useEffect(() => {
    void fetch();
  }, [fetch]);

  useEffect(() => {
    return () => {
      dispatch(resetVerifySlice());
    };
  }, [dispatch]);

  const handleEntryChange = useCallback(
    (index: number) => {
      if (!reviewData?.users) return;
      const des = reviewData?.users[index - 1];
      if (!des) return;
      const req = searchParams.get("req");
      navigate(
        `/summary/verify-entries/${des.idType}/${des.id}${
          req ? `?req=${req}` : ""
        }`,
      );
    },
    [reviewData?.users, searchParams, navigate],
  );

  const handleCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const item = {
        id: lbeidOrPartId as string,
        idType: String(idType),
      };
      if (event.target.checked) {
        dispatch(addToVerify(item));
      } else {
        dispatch(removeFromVerify(item));
      }
    },
    [dispatch, idType, lbeidOrPartId],
  );

  const unauthorized = useMemo(
    () => data?.restricted === true && data?.authorized === false,
    [data?.restricted, data?.authorized],
  );
  const paginationProps: PaginationProps = useMemo(
    () => ({
      total: reviewData?.users?.length ?? 1,
      value: reviewData?.users
        ? reviewData?.users.findIndex(id => id.id === lbeidOrPartId) + 1
        : 1,
      onChange: handleEntryChange,
    }),
    [handleEntryChange, reviewData?.users, lbeidOrPartId],
  );

  const checkboxProps: CheckboxProps = useMemo(
    () =>
      isPendingVerify
        ? {
            checked: true,
            disabled: true,
          }
        : {
            checked: toVerify.some(e => e.id === lbeidOrPartId),
            onChange: handleCheckbox,
          },
    [handleCheckbox, isPendingVerify, lbeidOrPartId, toVerify],
  );

  return {
    lbEntry: data?.lbEntry ?? null,
    sections: data?.sections ?? [],
    verifiable: data?.verifiable,
    preview: data?.preview,
    lbname: data?.lbname,
    lbid: data?.lbid,
    entryref: data?.entryref,
    idata: data?.idata,
    secdata: data?.secdata,
    paginationProps,
    checkboxProps,
    deleted: data?.deleted,
    status: data?.status,
    fetchRestricted,
    unauthorized,
    eventStatus: data?.eventStatus,
    entryDate: data?.entryDate,
    entryDateUTC: data?.entryDateUTC,
  };
};
